import React, { FunctionComponent } from 'react';
import { Box, Typography } from '@mui/material';
import { MoSpacing } from '@src/components/MoSpacing';
import { MoAvatar, SMALL_AVATAR_SIZE } from '@src/components/MoAvatar';
import { TimelineGroupedByDate, TimelineItemByTime, formatEventDescription } from '../utils';

/**
 * Returns the top margin spacing for a timeline item based on its position and type.
 * - First item: 0
 * - Consecutive messages from same sender grouping: 4 units
 * - Everything else (events, or messages after events): 2 units
 */
function getTimelineItemMarginTop(
  itemIndex: number,
  currentItem: TimelineItemByTime,
  prevItem: TimelineItemByTime | undefined,
): number {
  if (itemIndex === 0) return 0;
  if (currentItem.type === 'messages' && prevItem?.type === 'messages') return 4;
  return 2;
}

export type MessagesBoxViewProps = {
  groupedTimeline: TimelineGroupedByDate[];
  setLastMessageRef?: (node: HTMLSpanElement | null) => void;
  setMessagesRef?: (index: number, node: HTMLElement | null) => void;
};

function TimelineEventDisplay({ item }: { item: TimelineItemByTime }) {
  if (item.type !== 'event') return null;

  const description = formatEventDescription(item.event);

  return (
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
        fontStyle: 'italic',
        textAlign: 'center',
      }}
    >
      {description} • {item.formattedTime}
    </Typography>
  );
}

function TimelineMessagesDisplay({
  item,
  setLastMessageRef,
  setMessagesRef,
  isLastInDate,
}: {
  item: TimelineItemByTime;
  setLastMessageRef?: (node: HTMLSpanElement | null) => void;
  setMessagesRef?: (index: number, node: HTMLElement | null) => void;
  isLastInDate: boolean;
}) {
  if (item.type !== 'messages') return null;

  const { sender, formattedTime, messages } = item;

  return (
    <Box display="flex" gap={2}>
      <MoAvatar
        user={sender}
        size={SMALL_AVATAR_SIZE}
        sx={sender.inactiveAt ? { opacity: 0.5 } : undefined}
      />

      <div>
        <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', gap: 1.5 }}>
          <Box
            sx={{
              color: sender.inactiveAt ? 'grey.600' : 'text.primary',
              fontWeight: 600,
            }}
            component="span"
          >
            {sender.fullName}
          </Box>
          {formattedTime}
        </Typography>
        {messages.map((message, index) => (
          <Typography
            sx={{ whiteSpace: 'pre-line' }}
            key={message.id}
            variant="body1"
            data-id={message.id}
            ref={(node) => {
              const isLastMessage = isLastInDate && index === messages.length - 1;
              if (isLastMessage) {
                setLastMessageRef?.(node);
              }
              setMessagesRef?.(index, node);
            }}
          >
            {message.text}
          </Typography>
        ))}
      </div>
    </Box>
  );
}

const MessagesBoxView: FunctionComponent<MessagesBoxViewProps> = ({
  groupedTimeline,
  setLastMessageRef,
  setMessagesRef,
}) => {
  return (
    <MoSpacing y={4}>
      {groupedTimeline.map(({ formattedDate, items }, dateIndex) => (
        <Box key={formattedDate}>
          <Typography
            variant="body2"
            sx={{ mb: 2, textAlign: 'center', textTransform: 'uppercase', fontWeight: 600 }}
          >
            {formattedDate}
          </Typography>
          <div>
            {items.map((item, itemIndex) => {
              const isLastInDate =
                dateIndex === groupedTimeline.length - 1 && itemIndex === items.length - 1;
              const mt = getTimelineItemMarginTop(itemIndex, item, items[itemIndex - 1]);

              if (item.type === 'event') {
                return (
                  <Box key={item.event.id} sx={{ mt }}>
                    <TimelineEventDisplay item={item} />
                  </Box>
                );
              }

              return (
                <Box key={item.messages[0].id} sx={{ mt }}>
                  <TimelineMessagesDisplay
                    item={item}
                    setLastMessageRef={setLastMessageRef}
                    setMessagesRef={setMessagesRef}
                    isLastInDate={isLastInDate}
                  />
                </Box>
              );
            })}
          </div>
        </Box>
      ))}
    </MoSpacing>
  );
};

export default MessagesBoxView;
